import {
  CircularProgress,
  NavigationCard,
  ResourcePreviewList,
  Stack,
  Tag,
  Typography,
  WarningIcon,
} from '@all-manager/design-system-v2';
import type { PendingSubTopicWithPreview } from "../../../../types";
import ActivityTimelineItem from "../ActivityTimeline/ActivityTimelineItem";
import SubTopicActions from "../SubTopicActions/SubTopicActions";
import {
  MAX_PENDING_ITEMS,
  MAX_PENDING_QUOTES,
  MAX_REMINDERS,
  MAX_TASKS,
  usePendingSubTopicCard,
} from "./usePendingSubTopicCard";

type Props = {
  subTopic: PendingSubTopicWithPreview;
};

export default function PendingSubTopicCard(props: Props) {
  const hook = usePendingSubTopicCard(props);

  return (
    <NavigationCard
      title={props.subTopic.name ?? ''}
      href={hook.targetUrl}
      onClick={hook.handleTitleClick}
      tags={hook.hasOngoingIncident ? (
        <Tag color="incident" label="Incident en cours" icon={<WarningIcon fontSize="small" />} />
      ) : undefined}
      action={hook.isAdmin ? <SubTopicActions subTopic={props.subTopic} /> : undefined}
    >
      <ResourcePreviewList
        color="rappel"
        title="Rappels"
        maxItems={MAX_REMINDERS}
        items={hook.reminderItems.map((reminder) => ({
          id: reminder.id,
          label: reminder.label,
          meta: reminder.meta,
          tag: reminder.isOverdue ? <Tag color="incident" label="Date dépassée" variantIcon="state" /> : undefined,
        }))}
      />

      <ResourcePreviewList
        color="task"
        title="À faire"
        maxItems={MAX_TASKS}
        items={hook.taskItems}
      />

      <ResourcePreviewList
        color="pending_item"
        title="À suivre"
        maxItems={MAX_PENDING_ITEMS}
        items={hook.pendingItemItems}
      />

      <ResourcePreviewList
        color="documents"
        title="Devis en attente"
        maxItems={MAX_PENDING_QUOTES}
        items={hook.pendingQuoteItems}
      />

      {hook.activitiesLoading ? (
        <Stack alignItems="center">
          <CircularProgress size="small" aria-label="Chargement de l'historique" />
        </Stack>
      ) : hook.recentActivities.length === 0 ? (
        <Typography variant="noItem">Aucun historique</Typography>
      ) : (
        <Stack>
          {hook.recentActivities.map((action, index) => (
            <ActivityTimelineItem
              key={`${action.entity_type}-${action.id}`}
              item={action}
              isFirst={index === 0}
              isLast={index === hook.recentActivities.length - 1}
            />
          ))}
        </Stack>
      )}
    </NavigationCard>
  );
}
